import type { JSX } from 'react';

import { Grid } from '@change/ds-layout';

import { ClientRender } from '@change-corgi/core/react/ssr/render';
import { TrackingContext } from '@change-corgi/core/react/tracking';

import { SetPageStateValue } from 'src/shared/pageState';

import { usePetitionDetailsPageData, usePetitionDetailsPageUserData } from 'src/app/pages/petitionDetails/pageContext';
import { ResponsiveBox, SectionSeparator } from 'src/app/pages/petitionDetails/shared/components';
import { BeforeSuggestedPetitionsWaypoint } from 'src/app/pages/petitionDetails/shared/waypoints';
import { AppDownloadBottomBanner } from 'src/app/shared/components/appDownloadBottomBanner';
import { PetitionUnderReviewBanner } from 'src/app/shared/components/banners/petitionUnderReview';
import { MembershipBottomBanner } from 'src/app/shared/components/membershipBottomBanner';
import { SuggestedPetitionsLazy as SuggestedPetitionsLazySunrise } from 'src/app/shared/components/suggestedPetitionsLazySunrise';

import { useIsFullSunriseEnabled } from '../hooks/isFullSunriseEnabled';
import { MobileSignFormContextProvider } from '../hooks/mobileSignFormContext';
import { usePetitionPageStateData } from '../hooks/pageStateData';
import { useTrackPromotedView } from '../hooks/promotions';
import { PageScrollPositionProvider } from '../hooks/scroll';
import { useShowNewlyCreatedPetitions } from '../hooks/showNewlyCreatedPetitions';
import { useShowSuggestedVictoriousPetitions } from '../hooks/showSuggestedVictoriousPetitions';
import { useTrackPageView } from '../hooks/tracking/pageView';

import { DmFeedBannerContainer } from './DmFeedBanner';
import { ExpiredSessionBanner } from './ExpiredSessionBanner';
import { HeadContainer } from './head/Head';
import { HeadingContainer } from './Heading';
import { MainContainer } from './Main';
import { NativeShareMobile } from './NativeShareMobile';
import { RedirectUnpublished } from './RedirectUnpublished';
import { ScrollTrackingWaypointContainer } from './ScrollTrackingWaypoint';
import { StrengthTips } from './strengthTips/StrengthTips';

export function DetailsContainer(): JSX.Element {
	useTrackPageView();
	useTrackPromotedView();
	const showSuggestedVictoriousPetitions = useShowSuggestedVictoriousPetitions();
	const showNewlyCreated = useShowNewlyCreatedPetitions();
	const {
		data: { petition },
	} = usePetitionDetailsPageData();
	const petitionPageStateData = usePetitionPageStateData();
	const {
		data: { isMobileAppDownloadBannerEnabled, ownPetition },
	} = usePetitionDetailsPageUserData();
	const underReview = petition.status === 'UNDER_REVIEW';

	const {
		data: { isSunriseHeadingEnabled },
	} = useIsFullSunriseEnabled();

	return (
		<div data-testid="details-container" className="mb-8">
			<ExpiredSessionBanner />
			{underReview && <PetitionUnderReviewBanner />}
			<RedirectUnpublished />
			{/* FIXME: for consistency this should be rendered in DetailsWithContext with the other providers. */}
			<PageScrollPositionProvider>
				<SetPageStateValue name="petition" value={petitionPageStateData} />
				<HeadContainer />
				<DmFeedBannerContainer />
				<StrengthTips />
				{/* NativeShareMobile is client-only because it checks the user-agent, which is not available in SSR */}
				<ClientRender>
					<NativeShareMobile />
				</ClientRender>
				<Grid className="lg:!col-gap-6 lg:!row-gap-0 px-0 lg:max-w-[1376px] lg:!grid-cols-[repeat(14,minmax(0,1fr))] lg:!gap-0">
					<ResponsiveBox className="col-span-full lg:col-start-2 lg:-col-end-1" viewportSize="normal">
						{/* Replace the ifTrue expression with the Sunrise version */}
						{isSunriseHeadingEnabled ? <HeadingContainer /> : <HeadingContainer />}
					</ResponsiveBox>
					<div className="col-span-full">
						<MobileSignFormContextProvider>
							<MainContainer />
						</MobileSignFormContextProvider>
					</div>
				</Grid>
				<BeforeSuggestedPetitionsWaypoint />
				<ScrollTrackingWaypointContainer name="scroll_other_petitions_visible" />
				<div className="container max-w-[1376px]">
					<SectionSeparator />
				</div>
				<SuggestedPetitionsLazySunrise
					showVictorious={showSuggestedVictoriousPetitions}
					showNewlyCreated={showNewlyCreated}
					petitionSlugOrId={petition.id}
					channel="petition_footer"
					sourceLocation="petition_footer"
				/>
			</PageScrollPositionProvider>

			{/* MembershipBottomBanner is client-only because it checks the user-agent, which is not available in SSR */}
			<ClientRender>
				<MembershipBottomBanner slug={petition.slug} sourceLocation="petition_membership_popup" />
			</ClientRender>
			<TrackingContext properties={{ petition_id: `petition_${petition.id}` }}>
				<AppDownloadBottomBanner
					sourceLocation="petition"
					ownPetition={ownPetition}
					enabled={isMobileAppDownloadBannerEnabled}
				/>
			</TrackingContext>
		</div>
	);
}
